/**
 * ListingAskPanel — "Ask anything about this home" chat at the foot of the
 * Property Passport.
 *
 * Design notes (updated Feb 2026):
 *
 *  • Answers are grounded against this property's Passport where possible.
 *    Where the Passport doesn't have the answer, the assistant falls back to
 *    honest general knowledge (transit, geography, process questions) with a
 *    clear "general knowledge — verify locally" tail. The backend tells us
 *    which one we got via `grounding: "passport" | "general_knowledge"`.
 *
 *  • Suggested questions PERSIST across the session — a single conversation
 *    typically involves 3–5 follow-ups. We filter out chips the user has
 *    already asked so the rail keeps evolving as they explore.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Sparkles, Send, Loader2, ShieldCheck, Compass } from 'lucide-react';
import { Button } from './ui/button';
import { buildSuggestions } from '../utils/askSuggestions';

const API = process.env.REACT_APP_BACKEND_URL;

const _norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

const ListingAskPanel = ({ propertyId, property }) => {
  const [q, setQ] = useState('');
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(false);

  // Market + property-shape aware chip list. Recomputes only when the
  // property changes — cheap either way since it's a pure function.
  const suggestions = useMemo(() => buildSuggestions(property), [property]);

  // Hide chips the user has already asked so the rail always shows fresh
  // options. Keeps working across many turns in the same session.
  const askedSet = useMemo(
    () => new Set(thread.filter((m) => m.role === 'user').map((m) => _norm(m.text))),
    [thread],
  );
  const remainingSuggestions = suggestions.filter((s) => !askedSet.has(_norm(s)));

  const ask = async (raw) => {
    const text = (raw || q).trim();
    if (!text || loading) return;
    setQ('');
    setLoading(true);
    setThread((t) => [...t, { role: 'user', text }]);
    try {
      const r = await axios.post(`${API}/api/ai/ask-question`, {
        question: text,
        property_id: propertyId,
      });
      setThread((t) => [...t, {
        role: 'ai',
        text: r.data?.answer || 'No answer.',
        grounded: !!r.data?.grounded,
        grounding: r.data?.grounding || (r.data?.grounded ? 'passport' : 'general_knowledge'),
      }]);
    } catch (e) {
      setThread((t) => [...t, {
        role: 'ai',
        text: `Sorry — the assistant is unavailable right now (${e.response?.status || e.message}).`,
        grounded: false,
        error: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-t border-stone-200 bg-gradient-to-b from-white to-purple-50/30 px-6 py-5" data-testid="listing-ask-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-purple-700" />
        <span className="font-semibold text-stone-900">Ask anything about this home</span>
        <span className="text-[10px] text-stone-500 hidden sm:inline">grounded against this property&apos;s Passport</span>
      </div>

      {/* Conversation */}
      {thread.length > 0 && (
        <div className="space-y-2 mb-3 max-h-72 overflow-y-auto pr-1" data-testid="listing-ask-thread">
          {thread.map((m, i) => (
            m.role === 'user' ? (
              <div key={i} className="text-sm text-stone-700 bg-white rounded-lg border border-stone-200 px-3 py-2 max-w-[85%]" data-testid={`listing-ask-q-${i}`}>
                <span className="text-[10px] uppercase text-stone-400 font-semibold tracking-wide">You</span>
                <div>{m.text}</div>
              </div>
            ) : (
              <div key={i} className={`text-sm rounded-lg px-3 py-2 max-w-[95%] ml-auto ${m.error ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-purple-50 border border-purple-200 text-stone-800'}`} data-testid={`listing-ask-a-${i}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] uppercase text-purple-700 font-semibold tracking-wide">Passport Assistant</span>
                  {m.grounding === 'passport' && !m.error && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 bg-emerald-100 rounded px-1" data-testid={`listing-ask-badge-passport-${i}`}>
                      <ShieldCheck className="h-2.5 w-2.5" /> Passport-grounded
                    </span>
                  )}
                  {m.grounding === 'general_knowledge' && !m.error && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-800 bg-amber-100 rounded px-1" data-testid={`listing-ask-badge-general-${i}`}>
                      <Compass className="h-2.5 w-2.5" /> General knowledge
                    </span>
                  )}
                </div>
                <div className="whitespace-pre-wrap">{m.text}</div>
              </div>
            )
          ))}
          {loading && (
            <div className="text-sm text-purple-700 flex items-center gap-2 pl-2" data-testid="listing-ask-loading">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
            </div>
          )}
        </div>
      )}

      {/* Suggested prompts — persistent across the whole session so users
          can keep exploring. We filter out chips they've already asked. */}
      {remainingSuggestions.length > 0 && (
        <div className="mb-3" data-testid="listing-ask-suggestions">
          {thread.length > 0 && (
            <div className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold mb-1.5">
              More you can ask
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {remainingSuggestions.slice(0, thread.length > 0 ? 4 : 6).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => ask(s)}
                disabled={loading}
                data-testid={`listing-ask-suggested-${s.slice(0, 20).replace(/\W+/g, '-')}`}
                className="text-xs px-2.5 py-1 rounded-full bg-white border border-stone-200 hover:border-purple-300 hover:bg-purple-50 text-stone-700 transition disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); ask(); }}
        className="flex items-center gap-2"
      >
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask about this home…"
          disabled={loading}
          data-testid="listing-ask-input"
          className="flex-1 text-sm px-3 py-2 rounded-lg border border-stone-300 bg-white focus:outline-none focus:border-purple-500"
        />
        <Button
          type="submit"
          size="sm"
          disabled={loading || !q.trim()}
          data-testid="listing-ask-submit"
          className="bg-purple-700 hover:bg-purple-800 text-white"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
      <div className="text-[10px] text-stone-400 mt-2">
        Answers cite Passport sections where possible; general-knowledge answers are flagged and should be verified on official registers before bidding.
      </div>
    </div>
  );
};

export default ListingAskPanel;
